import { ButtonBaseProps, CircularProgress } from '@mui/material';
import { ChevronDown, CircleX } from 'lucide-react';
import {
  forwardRef,
  KeyboardEvent as ReactKeyboardEvent,
  MouseEvent as ReactMouseEvent,
  ReactNode,
} from 'react';
import {
  SelectTriggerRoot,
  SelectTriggerVariant,
  TriggerChevron,
  TriggerClearButton,
  TriggerContent,
  TriggerIconSlot,
  TriggerLabel,
  TriggerSpinner,
} from './select-trigger.elements';

export interface SelectTriggerProps extends Omit<ButtonBaseProps, 'content'> {
  variant?: SelectTriggerVariant;
  /** Drives the open look; the caller owns the menu state. */
  open?: boolean;
  /** Leading glyph, clamped to the slot's footprint whatever it renders. */
  icon?: ReactNode;
  iconColor?: string;
  /** Single-line text slot, truncated with an ellipsis. */
  label?: ReactNode;
  /** Replaces the text slot with content that may wrap, e.g. a chip list. */
  content?: ReactNode;
  /** Replaces the trailing slot with a spinner, e.g. while a save is in flight. */
  loading?: boolean;
  /** Renders the clear button; it appears on hover or focus. */
  onClear?: () => void;
  /**
   * The row shows a value nobody can change here. Keeps the geometry, drops the
   * button affordances — no hover surface, no focus ring, no chevron.
   */
  readOnly?: boolean;
}

/**
 * The shared control that opens a menu or shows a single property's value. It
 * owns the geometry every side-panel row is expected to line up with — icon
 * footprint, text baseline, trailing slot — so a row never restates it.
 */
export const SelectTrigger = forwardRef<HTMLButtonElement, SelectTriggerProps>(
  (
    {
      variant = 'outlined',
      open,
      icon,
      iconColor,
      label,
      content,
      loading,
      onClear,
      readOnly,
      ...rest
    },
    ref,
  ) => (
    <SelectTriggerRoot
      {...rest}
      ref={ref}
      open={open}
      variant={variant}
      readOnly={readOnly}
      component={readOnly ? 'span' : 'button'}
      disableRipple={readOnly || rest.disableRipple}
      tabIndex={readOnly ? -1 : rest.tabIndex}
      onKeyDown={(event: ReactKeyboardEvent<HTMLButtonElement>) => {
        // The clear button is hidden from assistive tech and only appears on
        // hover, so the trigger itself is the keyboard route to clearing.
        if (onClear && !loading && (event.key === 'Backspace' || event.key === 'Delete')) {
          event.preventDefault();
          onClear();
          return;
        }
        rest.onKeyDown?.(event);
      }}
    >
      {icon ? (
        <TriggerIconSlot aria-hidden sx={{ color: iconColor }}>
          {icon}
        </TriggerIconSlot>
      ) : null}
      {content ? (
        <TriggerContent>{content}</TriggerContent>
      ) : (
        <TriggerLabel>{label}</TriggerLabel>
      )}
      {loading ? (
        <TriggerSpinner aria-hidden data-testid="select-trigger-spinner">
          <CircularProgress size="1rem" color="inherit" />
        </TriggerSpinner>
      ) : (
        <>
          {onClear ? (
            <TriggerClearButton
              aria-hidden
              data-trigger-clear
              data-testid="select-trigger-clear"
              onClick={(event: ReactMouseEvent<HTMLSpanElement>) => {
                event.stopPropagation();
                onClear();
              }}
            >
              <CircleX />
            </TriggerClearButton>
          ) : null}
          {variant !== 'inline' && !readOnly ? (
            <TriggerChevron aria-hidden data-testid="select-trigger-chevron">
              <ChevronDown />
            </TriggerChevron>
          ) : null}
        </>
      )}
    </SelectTriggerRoot>
  ),
);

SelectTrigger.displayName = 'SelectTrigger';
